import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ClientService {

  private readonly http = inject(HttpClient);

  changeDynamicNavRowOrder(params: object): Observable<any> {
    return this.http.post(`${environment.api.host_path}/dynamic-navigation/set-sorting`, params);
  }

  importKeymatchCsv(clientId: string, fileCsv: any){
    const params = new HttpParams().set('clientId', clientId);
    return this.http.post(`${environment.api.host_path}/keymatch/import`, fileCsv, {params});
  }

  exportKeymatchCsv(id: string): Observable<any> {
    return this.http.get(`${environment.api.host_path}/keymatch/export/${id}`, {responseType: 'text'});
  }

  setAutolearning(id: string, autolearning: boolean): Observable<any> {
    const params = new HttpParams().set('autolearning', autolearning);
    return this.http.patch<any>(`${environment.api.host_path}/client/${id}/autolearning`, {}, { params });
  }

  getClientById(id: string): Observable<any> {
    return this.http.get<any>(`${environment.api.host_path}/client/${id}`);
  }

  /**
   * get list of clients (paginated)
   */
  getClientsList(pageIndex: number, pageSize: number, sortBy: string, clientFilter: string): Observable<any> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('search', clientFilter ?? '');
    return this.http.get<any>(`${environment.api.host_path}/client/list`, { params });
  }

  getClientListAll() {
    return this.http.get(`${environment.api.host_path}/client/listAll`);
  }

  /**
   * add new client
   */
  addNewClient(params: any): Observable<any> {
    return this.http.post(`${environment.api.host_path}/client`, params);
  }

  editClient(params: any): Observable<any> {
    return this.http.put(`${environment.api.host_path}/client`, params);
  }

  deleteClient(id: number): Observable<any> {
    return this.http.delete(`${environment.api.host_path}/client/${id}`);
  }

  getDynamicNavigationList(pageIndex: number, pageSize: number, sortBy: string, clientId: string, dynamicFilter: any): Observable<any> {
    
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('clientId', clientId ?? '')

      if(dynamicFilter.label) {
        params = params.set('label', dynamicFilter.label);
      }
      if(dynamicFilter.fieldName) {
        params = params.set('meta', dynamicFilter.fieldName);
      }

    return this.http.get(`${environment.api.host_path}/dynamic-navigation/list`, { params });
  }

  /**
   * ONEBOX list — paginated
   */
  getOneBoxList(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    clientId: string | number | null,
    oneBoxFilter: string | { search?: string }
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy);

    // clientId (flat)
    const cid = (clientId ?? '').toString().trim();
    if (cid) params = params.set('clientId', cid);

    // normalizza search
    let search = '';
    if (typeof oneBoxFilter === 'string') {
      search = (oneBoxFilter ?? '').trim();
    } else if (oneBoxFilter && typeof oneBoxFilter === 'object' && typeof oneBoxFilter.search === 'string') {
      search = oneBoxFilter.search.trim();
    }
    if (search) params = params.set('search', search);

    return this.http.get(`${environment.api.host_path}/onebox/list`, { params });
  }

  getOneBoxListAll() {
    return this.http.get(`${environment.api.host_path}/onebox/listAll`);
  }

  getRelatedQueryList(pageIndex: number, pageSize: number, sortBy: string, clientId: string, relatedQueryFilter: string): Observable<any> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('clientId', clientId ?? '')
      .set('search', (relatedQueryFilter ?? ''));
    return this.http.get(`${environment.api.host_path}/related-query/list`, { params });
  }

  getKeymatchList(pageIndex: number, pageSize: number, sortBy: string, clientId: string, keymatchFilter: string): Observable<any> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('clientId', clientId ?? '')
      .set('search', (keymatchFilter ?? ''));
    return this.http.get(`${environment.api.host_path}/keymatch/list`, { params });
  }

  getExcludeUrlList(pageIndex: number, pageSize: number, sortBy: string, clientId: string, oneBoxFilter: string): Observable<any> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('clientId', clientId ?? '')
      .set('search', (oneBoxFilter ?? ''));
    return this.http.get(`${environment.api.host_path}/url-client-exclusion/list`, { params });
  }

  getPolicyDiffList(pageIndex: number, pageSize: number, sortBy: string, clientId: string, oneBoxFilter: string): Observable<any> {
    const params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('clientId', clientId ?? '')
      .set('search', (oneBoxFilter ?? ''));
    return this.http.get(`${environment.api.host_path}/biasing/list`, { params });
  }

  // meta (field) — listAll
  getAllFields() {
    return this.http.get(`${environment.api.host_path}/field/listAll`);
  }

  // alias — listAll
  getAllAlias() {
    return this.http.get(`${environment.api.host_path}/alias/listAll`);
  }

  addNewDynamicNavigation(params: any) {
    return this.http.post(`${environment.api.host_path}/dynamic-navigation`, params);
  }

  editDynamicNavigation(params: any) {
    return this.http.put(`${environment.api.host_path}/dynamic-navigation`, params);
  }

  deleteDynamicNavigation(id: number) {
    return this.http.delete(`${environment.api.host_path}/dynamic-navigation/${id}`);
  }

  addKeymatch(params: any) {
    return this.http.post(`${environment.api.host_path}/keymatch`, params);
  }

  editKeymatch(params: any) {
    return this.http.put(`${environment.api.host_path}/keymatch`, params);
  }

  deleteKeymatch(id: number) {
    return this.http.delete(`${environment.api.host_path}/keymatch/${id}`);
  }

  addOnebox(params: any) {
    return this.http.post(`${environment.api.host_path}/onebox`, params);
  }

  editOnebox(params: any) {
    return this.http.put(`${environment.api.host_path}/onebox`, params);
  }

  deleteOnebox(id: number) {
    return this.http.delete(`${environment.api.host_path}/onebox/${id}`);
  }

  addRelatedQuery(params: any) {
    return this.http.post(`${environment.api.host_path}/related-query`, params);
  }

  editRelatedQuery(params: any) {
    return this.http.put(`${environment.api.host_path}/related-query`, params);
  }

  deleteRelatedQuery(id: number) {
    return this.http.delete(`${environment.api.host_path}/related-query/${id}`);
  }

  addExcludeUrl(params: any) {
    return this.http.post(`${environment.api.host_path}/url-client-exclusion`, params);
  }

  editExcludeUrl(params: any) {
    return this.http.put(`${environment.api.host_path}/url-client-exclusion`, params);
  }

  deleteExcludeUrl(id: number) {
    return this.http.delete(`${environment.api.host_path}/url-client-exclusion/${id}`);
  }

  associateBiasingPolicy(idClient: any, idBiasing: number) {
    return this.http.put(`${environment.api.host_path}/client/${idClient}/biasing/${idBiasing}`, {});
  }

  associateOnebox(idClient: any, idOnebox: number) {
    return this.http.put(`${environment.api.host_path}/client/${idClient}/onebox/${idOnebox}`, {});
  }

  removeBiasingPolicyFromClient(idClient: any, idBiasing: number) {
    return this.http.delete(`${environment.api.host_path}/client/${idClient}/biasing/${idBiasing}`);
  }

  removeOneboxFromClient(idClient: any, idOnebox: number) {
    return this.http.delete(`${environment.api.host_path}/client/${idClient}/onebox/${idOnebox}`);
  }

  addBiasingPolicy(params: any) {
    return this.http.post(`${environment.api.host_path}/biasing`, params);
  }

  deleteBiasingPolicy(id: number) {
    return this.http.delete(`${environment.api.host_path}/biasing/${id}`);
  }

  addKeyword(idOneBox: number, keyword: string) {
    return this.http.post(`${environment.api.host_path}/onebox-keyword`, { idOneBox, keyword });
  }

  /**
   * ONEBOX keyword list — paginated
   */
  getKeywordList(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    oneBoxId: number,
    keywordFilter: string
  ): Observable<any> {
    let params = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize)
      .set('sortBy', sortBy)
      .set('oneBoxId', oneBoxId);

    const q = (keywordFilter ?? '').trim();
    params = params.set('keywordFilter', keywordFilter ?? '');
    if (q) {
      params = params.set('search', q);
    }

    return this.http.get(`${environment.api.host_path}/onebox-keyword/list`, { params });
  }

  deleteKeyword(id: number) {
    return this.http.delete(`${environment.api.host_path}/onebox-keyword/${id}`);
  }

  editKeyword(params: any) {
    return this.http.put(`${environment.api.host_path}/onebox-keyword`, params);
  }

}